import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import useAnnotation from 'hooks/useAnnotation'
import ViewerFilePreview from 'pages/building/buildingDetailViewMode/components/ViewerFilePreview'
import { useRecoilValue } from 'recoil'
import { hasLinkErrorState } from 'store/buildingStore'
import { Alert, AlertApiResponse, AlertDraftUser } from 'types/alert'
import { AnnotationType, BuildingFile } from 'types/building'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'
import Title from 'components/atoms/Title'

import ModalAlertSetting from './ModalAlertSetting/ModalAlertSetting'
import AnnotationSetting from './AnnotationSetting'
import AnnotationTypeSelector from './AnnotationTypeSelector'
import ConfirmChangeAnnotation from './ConfirmChangeAnnotation'
import FileList from './FileList'
import MemoSetting from './MemoSetting'
import ModalDeleteAnnotation from './ModalDeleteAnnotation'

export default function AnnotationModal() {
  const { t } = useTranslation()
  const { id, floorId } = useParams()
  const [isAddingFile, setIsAddingFile] = useState(false)
  const [isChangeType, setIsChangeType] = useState(false)
  const [isOpenAlertSetting, setIsOpenAlertSetting] = useState(false)
  const [openModal, setOpenModal] = useState<string>('')
  const [existsFile, setExistsFile] = useState<BuildingFile[]>([])
  const [selectedFiles, setSelectedFiles] = useState<BuildingFile[]>([
    ...existsFile
  ])
  const [memoName, setMemoName] = useState('')
  const [memoContent, setMemoContent] = useState('')
  const [disableWhenMemoNotCompleted, setDisableWhenMemoNotCompleted] =
    useState(true)
  const [previewFileIndex, setPreviewFileIndex] = useState<number | undefined>(
    undefined
  )

  const {
    form,
    isLoading,
    isFetchingContent,
    setIsFetchingContent,
    isDataExistsAnnotation,
    dataSource,
    currentSelected,
    setCurrentSelected,
    childrenFolder,
    selectedFolder,
    setSelectedFolder,
    selectedChildrenFolder,
    setSelectedChildrenFolder,
    handleBack,
    handleCloseModal,
    handleSubmit,
    tempAlertData,
    setTempAlertData
  } = useAnnotation()

  const hasLinkError = useRecoilValue(hasLinkErrorState)

  const changeSelectedFolder = (value: AnnotationType) => {
    if (value !== selectedFolder) {
      setSelectedFiles([])
      setExistsFile([])
      setCurrentSelected((prev) => ({
        ...prev,
        type: value
      }))
      if (!isDataExistsAnnotation) {
        setIsChangeType(true)
      } else {
        setSelectedFolder(value)
      }
    }
  }

  const handleCloseFileSelectMenu = (clear?: boolean) => {
    if (clear) {
      setSelectedFiles([])
    } else {
      const filesPrev =
        currentSelected?.files?.filter(
          (file) => !existsFile.map((item) => item.id).includes(file.id)
        ) || []
      setCurrentSelected((prev) => ({
        ...prev,
        files: [...filesPrev, ...selectedFiles]
      }))
    }
    setIsAddingFile(false)
  }

  const handleSelectFile = (file: BuildingFile) => {
    const existingEntry = selectedFiles.find(
      (item) => item.id === file.id || item.name === file.name
    )

    if (existingEntry) {
      setSelectedFiles((prev) =>
        prev.filter((item) => item.id !== existingEntry.id)
      )
      return
    }

    const overwrittenEntry = currentSelected?.files?.find(
      (item) => item.name === file.name
    )

    setSelectedFiles((prev) => [
      ...prev,
      {
        ...(overwrittenEntry || file),
        category: selectedChildrenFolder?.id
      }
    ])
  }

  const handleMemoNameChange = (name: string) => {
    setMemoName(name)
    setCurrentSelected((prev) => ({
      ...prev,
      memo_name: name
    }))
  }

  useEffect(() => {
    if (existsFile.length) {
      setSelectedFiles([...existsFile])
    }
  }, [existsFile])

  useEffect(() => {
    if (currentSelected?.memo_name) {
      setMemoName(currentSelected.memo_name)
    }
  }, [currentSelected, currentSelected?.memo_name])

  useEffect(() => {
    // custom placeholder of popup link
    const tooltipInput = document.querySelector('.ql-tooltip input[data-link]')
    if (tooltipInput) {
      tooltipInput.setAttribute('data-link', 'https://')
      tooltipInput.setAttribute('placeholder', 'https://')
    }
  }, [currentSelected, selectedFolder])

  const initialAlertData = useMemo(() => {
    if (!tempAlertData) return undefined

    const repeatOption: 'repeat' | 'none' =
      tempAlertData.repeat_type === 2 ? 'repeat' : 'none'

    let repeatType: 'weekly' | 'monthly' | 'yearly' = 'weekly'
    if (tempAlertData.repeat_unit_type === 2) repeatType = 'monthly'
    if (tempAlertData.repeat_unit_type === 3) repeatType = 'yearly'

    let endType: 'never' | 'on_date' | 'after_occurrences' = 'never'
    if (tempAlertData.end_type === 2) endType = 'on_date'
    if (tempAlertData.end_type === 3) endType = 'after_occurrences'

    return {
      title: tempAlertData.title,
      alertDateTime: tempAlertData.send_at,
      // Map persistenced detailed user data back to initial alert state
      users: (tempAlertData.extra_users_data || []).map(
        (u: AlertDraftUser) => ({
          userId: u.userId,
          email: u.email,
          lastName: u.lastName,
          firstName: u.firstName,
          organizationName: u.organizationName,
          permission: u.permission
        })
      ),
      repeatSchedule: {
        option: repeatOption,
        interval: tempAlertData.repeat_unit,
        type: repeatType,
        weekDays: tempAlertData.repeat_week_days,
        endType,
        endDate: tempAlertData.end_date ?? undefined,
        occurrences: tempAlertData.end_count ?? undefined
      },
      content: tempAlertData.content,
      body_content: tempAlertData.body_content
    } as Partial<Alert>
  }, [tempAlertData])

  return (
    <>
      <Modal
        width="1200px"
        rootClassName="shared-permission-modal"
        onCancel={handleCloseModal}
      >
        {isAddingFile ? (
          <FileList
            folder={dataSource}
            selectedChildrenFolder={selectedChildrenFolder}
            selectedFiles={selectedFiles}
            handleSelectFile={handleSelectFile}
            handleCloseFileSelectMenu={handleCloseFileSelectMenu}
          />
        ) : (
          <div>
            <div className="flex flex-col w-full">
              <Title
                className="mb-[10px]"
                height={28.96}
                title={t('building.titleAddAnnotation')}
              />

              <AnnotationTypeSelector
                changeSelectedFolder={changeSelectedFolder}
                selectedChildrenFolder={selectedChildrenFolder}
                selectedFolder={selectedFolder}
                t={t}
                hasAlert={!!currentSelected?.alert?.id || !!tempAlertData}
                onOpenAlertSetting={() => setIsOpenAlertSetting(true)}
              />

              {selectedFolder === AnnotationType.MEMO ? (
                <MemoSetting
                  memo={currentSelected?.memo}
                  memoName={memoName}
                  onMemoNameChange={handleMemoNameChange}
                  onMemoContentChange={setMemoContent}
                  setDisableWhenMemoNotCompleted={
                    setDisableWhenMemoNotCompleted
                  }
                  setIsFetchingContent={setIsFetchingContent}
                />
              ) : (
                <AnnotationSetting
                  form={form}
                  selectedFolder={selectedFolder}
                  selectedChildrenFolder={selectedChildrenFolder}
                  childrenFolder={childrenFolder}
                  dataSource={dataSource}
                  setSelectedChildrenFolder={setSelectedChildrenFolder}
                  handleBack={handleBack}
                  setExistsFile={setExistsFile}
                  setIsAddingFile={setIsAddingFile}
                  setPreviewFileIndex={setPreviewFileIndex}
                />
              )}
            </div>
            <div className="flex w-full items-end pt-5 pl-5 pr-[30px] mt-[38px] justify-between border-0 border-t border-solid border-[#EEEFF2] mx-2">
              {currentSelected?.id && (
                <Button
                  danger
                  type="primary"
                  onClick={() => setOpenModal('deleted')}
                  sizing="w-[140px] h-[50px]"
                >
                  {t('common.titleDeleteAnnotation')}
                </Button>
              )}

              <div className="flex gap-10 ml-auto">
                <Button sizing="w-[140px] h-[50px]" onClick={handleCloseModal}>
                  {t('common.cancel')}
                </Button>
                <Button
                  type="primary"
                  sizing="w-[140px] h-[50px]"
                  className="theme-bg-primary"
                  loading={isLoading || isFetchingContent}
                  onClick={() =>
                    handleSubmit(undefined, undefined, memoContent)
                  }
                  disabled={
                    (selectedFolder === AnnotationType.MEMO &&
                      (disableWhenMemoNotCompleted || isFetchingContent)) ||
                    (selectedChildrenFolder?.id === 40 && hasLinkError)
                  }
                >
                  {t('common.confirm')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </Modal>
      {openModal === 'deleted' && (
        <ModalDeleteAnnotation
          setIsOpen={setOpenModal}
          handleCloseModal={handleCloseModal}
        />
      )}
      {isChangeType && (
        <ConfirmChangeAnnotation
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          setIsOpenConfirm={setIsChangeType}
        />
      )}
      {isOpenAlertSetting && (
        <ModalAlertSetting
          handleCloseModal={() => setIsOpenAlertSetting(false)}
          onSaveSuccess={(res: AlertApiResponse) => {
            if (currentSelected) {
              const newAlertId = res?.id || undefined
              if (newAlertId) {
                // Update current annotation with the new alert ID
                setCurrentSelected({
                  ...currentSelected,
                  alert: { id: newAlertId }
                })
              }
            }
            setIsOpenAlertSetting(false)
          }}
          onDeleteSuccess={() => {
            if (currentSelected) {
              setCurrentSelected({ ...currentSelected, alert: undefined })
            }
            setIsOpenAlertSetting(false)
          }}
          buildingId={id}
          floorId={floorId ? Number(floorId) : undefined}
          annotationId={
            currentSelected?.id ? String(currentSelected.id) : undefined
          }
          pointId={currentSelected?.point_id}
          alertId={currentSelected?.alert?.id}
          onSaveDraft={(payload) => {
            setTempAlertData(payload)
            setIsOpenAlertSetting(false)
          }}
          initialData={initialAlertData}
        />
      )}

      {previewFileIndex !== undefined && (
        <ViewerFilePreview
          fileIndex={previewFileIndex}
          file={existsFile[previewFileIndex]}
          maxLength={existsFile.length}
          setPreviewFileIndex={setPreviewFileIndex}
        />
      )}
    </>
  )
}
